import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { useWebSocketMessages } from "../../hooks/useWebSocket";
import { LLMConversationEntry, LLMConversationHistory } from "../../types";
import { ProcessorType } from "../../types/processor";
import api from "../../services/api";
import { ConversationMessage, ProcessorMessage } from "./ProcessorMessage";

interface ConversationViewProps {
  workflowId: string;
  workflowStatus?: string;
}

/**
 * Conversation View for workflow observability (SWRM-164).
 *
 * Displays all workflow events (processor lifecycle + LLM messages) as a
 * chat-style conversation with real-time updates via WebSocket.
 *
 * Active processor tracking: Uses conversation message flow (processor_start/complete/failed)
 * to determine which processors are currently running. This is more reliable than polling
 * the agent_executions API because it updates immediately with WebSocket messages.
 */
export const ConversationView: React.FC<ConversationViewProps> = ({
  workflowId,
  workflowStatus,
}) => {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true); // Ref version to avoid WebSocket handler re-registration
  const [humanInputValue, setHumanInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number>(0);

  // Track active processors based on conversation flow (SWRM-164)
  // This is more reliable than agentExecutions because it updates immediately with conversation messages
  const [activeProcessors, setActiveProcessors] = useState<Set<string>>(
    new Set()
  );

  // Check if workflow is waiting for human input
  const isWaitingForInput =
    workflowStatus === "waiting_approval" ||
    workflowStatus === "WAITING_APPROVAL";

  /**
   * Submit human input response and resume workflow.
   */
  const handleSubmitHumanInput = useCallback(async () => {
    if (!humanInputValue.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Submit the human input
      await api.submitHumanInput(workflowId, humanInputValue.trim());

      // Automatically resume the workflow to continue with the human input
      await api.resumeWorkflow(workflowId);

      setHumanInputValue(""); // Clear input after successful submission
      // The workflow will resume and WebSocket will update the UI
    } catch (err) {
      console.error("Failed to submit human input or resume workflow:", err);
      setError("Failed to submit response. Please try again.");
      setTimeout(() => setError(null), 5000); // Clear error after 5 seconds
    } finally {
      setIsSubmitting(false);
    }
  }, [workflowId, humanInputValue, isSubmitting]);

  /**
   * Extract step_id from agent_name format: "Step Name [step_id]"
   */
  const extractStepId = useCallback((agentName: string): string => {
    const match = agentName.match(/\[([^\]]+)\]/);
    return match ? match[1] : "";
  }, []);

  /**
   * Calculate indentation level from step_id depth.
   * Examples:
   * - "input" = 0 (no dots)
   * - "orchestrator.subagent" = 1 (one dot)
   * - "orchestrator.subagent.processor" = 2 (two dots)
   */
  const calculateIndentLevel = useCallback((stepId: string): number => {
    if (!stepId) return 0;
    const dots = (stepId.match(/\./g) || []).length;
    return dots;
  }, []);

  /**
   * Map agent_type (Python class name) to ProcessorType.
   */
  const mapAgentTypeToProcessorType = useCallback(
    (agentType: string): ProcessorType => {
      // Map based on known processor class names (case-insensitive)
      const lowerType = agentType.toLowerCase();
      if (lowerType.includes("orchestrator")) return "ORCHESTRATOR";
      if (lowerType.includes("agentic")) return "AGENTIC";
      if (lowerType.includes("oneshot")) return "SCRIPTED"; // oneshot is SCRIPTED (blue)
      if (
        lowerType.includes("workspace") ||
        lowerType.includes("code") ||
        lowerType.includes("shell")
      )
        return "UTILITY";
      // Default to SCRIPTED (includes input processor)
      return "SCRIPTED";
    },
    []
  );

  /**
   * Update active processors set based on message type (SWRM-164).
   * Called when processing both initial conversation load and WebSocket messages.
   */
  const updateActiveProcessors = useCallback((entry: LLMConversationEntry) => {
    const stepId = entry.agent_name.match(/\[([^\]]+)\]/)?.[1];
    if (!stepId) return;

    if (
      entry.message_type === "processor_start" ||
      entry.message_type === "request"
    ) {
      // Processor started - add to active set
      setActiveProcessors((prev) => new Set(prev).add(stepId));
    } else if (
      entry.message_type === "processor_complete" ||
      entry.message_type === "processor_failed"
    ) {
      // Processor finished - remove from active set
      setActiveProcessors((prev) => {
        const next = new Set(prev);
        next.delete(stepId);
        return next;
      });
    }
  }, []);

  /**
   * Transform LLM conversation entries into conversation messages.
   */
  const transformEntries = useCallback(
    (entries: LLMConversationEntry[]): ConversationMessage[] => {
      const transformedMessages: ConversationMessage[] = [];

      for (const entry of entries) {
        // SWRM-164: Update active processors tracking
        updateActiveProcessors(entry);

        // SWRM-164: Skip the error message box for human input requests (we'll style the actual orchestrator message instead)
        if (
          entry.message_type === "error" &&
          entry.response_content?.includes("HUMAN INPUT REQUESTED")
        ) {
          continue; // Skip this message
        }

        // Skip tool_response messages - the agent already acknowledges tool completion
        if (entry.message_type === "tool_response") {
          continue;
        }

        // Don't skip processor_complete messages - show them with grey styling for observability

        const stepId = extractStepId(entry.agent_name);
        const processorType = mapAgentTypeToProcessorType(entry.agent_type);
        const indentLevel = calculateIndentLevel(stepId);

        // Determine content based on message type
        let content = "";

        // SWRM-164: Skip LLM request messages if we already have a message from this step_id
        // (avoids duplicates when processors log both processor_start and LLM request)
        if (entry.message_type === "request") {
          const existingMessagesFromStep = transformedMessages.some(
            (m) => m.stepId === stepId
          );
          if (existingMessagesFromStep) {
            continue; // Skip - processor_start event already logged
          }
          // Otherwise, transform the request to a friendly start message
          content = `Starting ${entry.agent_type}`;
        } else if (entry.message_type === "processor_start") {
          // SWRM-164: Transform processor_start messages
          const lowerAgentType = entry.agent_type.toLowerCase();
          if (lowerAgentType.includes("orchestrator")) {
            // Check if this is a resume (orchestrator with prior messages)
            const priorMessages = transformedMessages.filter(
              (m) => m.agentName === entry.agent_name
            );
            content =
              priorMessages.length > 0
                ? "" // no extra message for resuming orchestration
                : "Starting orchestration";
          } else {
            // Use the message as-is for other processors
            content = entry.response_content || "Starting processor";
          }
        } else if (entry.message_type === "processor_failed") {
          // Use the message as-is
          content = entry.response_content || "";
        } else if (entry.response_content) {
          // SWRM-164: Special handling for oneshot document responses - show preview
          if (
            entry.message_type === "response" &&
            entry.agent_type.toLowerCase().includes("oneshot")
          ) {
            const preview = entry.response_content
              .replace(/\n+/g, " ") // Replace newlines with spaces
              .replace(/\s+/g, " ") // Collapse multiple spaces
              .trim()
              .substring(0, 150); // Take first 150 chars
            content = `Generated a document starting with "${preview}..."`;
          } else {
            // SWRM-164: Check if response contains a tool call and show both message and tool call
            try {
              const responseData =
                typeof entry.response_content === "string"
                  ? JSON.parse(entry.response_content)
                  : entry.response_content;

              if (responseData?.tool_call?.name) {
                // This is an LLM response with a tool call - show both thinking and tool call
                const thinking = responseData.text_content || responseData.text || responseData.message || responseData.content || '';
                const toolName = responseData.tool_call.name;
                
                if (thinking) {
                  content = `${thinking}\n\n📞 Calling tool: ${toolName}`;
                } else {
                  content = `Calling tool ${toolName}`;
                }
              } else {
                // Regular response content
                content = entry.response_content;
              }
            } catch {
              // Not JSON or parsing failed - use as-is
              content = entry.response_content;
            }
          }
        } else if (entry.prompt) {
          content = entry.prompt;
        } else if (entry.system_prompt) {
          content = entry.system_prompt;
        }

        // Check if this is a user message
        const isUserMessage =
          entry.message_type === "human_input" && content.includes("👤");

        if (content) {
          transformedMessages.push({
            id: entry.id,
            agentName: entry.agent_name,
            stepId,
            processorType,
            messageType: entry.message_type,
            content,
            timestamp: entry.timestamp,
            indentLevel,
            isUserMessage,
          });
        }
      }

      return transformedMessages;
    },
    [
      extractStepId,
      mapAgentTypeToProcessorType,
      calculateIndentLevel,
      updateActiveProcessors,
    ]
  );

  /**
   * Check if a processor is currently active AND this is the last message overall.
   * Simplified logic: Only the LAST message in the entire conversation can show bouncing balls.
   * This prevents multiple messages from showing bouncing balls simultaneously.
   */
  const isProcessorActive = useCallback(
    (stepId: string, messageId: number): boolean => {
      if (!stepId) return false;

      // Only the LAST message overall can show bouncing balls
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage || lastMessage.id !== messageId) {
        return false;
      }

      // Additional check: the last message must NOT be a completion or failure
      if (
        lastMessage.messageType === 'processor_complete' ||
        lastMessage.messageType === 'processor_failed'
      ) {
        return false;
      }

      // Check if this processor is in our active set
      const isActive = activeProcessors.has(stepId);
      
      // DEBUG: Log when showing bouncing balls
      if (isActive) {
        console.log('[ConversationView] Showing bouncing balls for:', {
          messageId,
          stepId,
          agentName: lastMessage.agentName,
          messageType: lastMessage.messageType,
          activeProcessorsCount: activeProcessors.size,
          activeProcessors: Array.from(activeProcessors)
        });
      }
      
      return isActive;
    },
    [activeProcessors, messages]
  );

  /**
   * Check if user is scrolled to bottom.
   */
  const checkIfAtBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const threshold = 100; // pixels from bottom
    const atBottom = scrollHeight - scrollTop - clientHeight < threshold;
    isAtBottomRef.current = atBottom; // Update ref for WebSocket handler
    return atBottom;
  }, []);

  /**
   * Scroll to bottom of conversation.
   */
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setNewMessageCount(0);
    }
  }, []);

  /**
   * Handle scroll events to track if user is at bottom.
   */
  const handleScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    if (atBottom) {
      setNewMessageCount(0);
    }
  }, [checkIfAtBottom]);

  /**
   * Fetch initial conversation history.
   */
  useEffect(() => {
    const fetchConversation = async () => {
      try {
        console.log(
          "[ConversationView] Fetching conversation for workflow:",
          workflowId
        );
        setLoading(true);

        // Use api service to fetch conversation
        const data: LLMConversationHistory =
          await api.getWorkflowLLMConversation(workflowId);
        console.log(
          "[ConversationView] Fetched",
          data.entries.length,
          "entries"
        );
        const transformedMessages = transformEntries(data.entries);
        console.log(
          "[ConversationView] Transformed to",
          transformedMessages.length,
          "messages"
        );
        setMessages(transformedMessages);

        // Track last message ID
        if (transformedMessages.length > 0) {
          lastMessageIdRef.current =
            transformedMessages[transformedMessages.length - 1].id;
        }

        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load conversation"
        );
        console.error("Error fetching conversation:", err);
      } finally {
        setLoading(false);
      }
    };

    if (workflowId) {
      console.log(
        "[ConversationView] useEffect triggered - workflowId:",
        workflowId
      );
      fetchConversation();
    }
  }, [workflowId, transformEntries]);

  /**
   * Auto-scroll to bottom when new messages arrive (if user is already at bottom).
   */
  useEffect(() => {
    console.log(
      "[ConversationView] Auto-scroll effect triggered - messages.length:",
      messages.length,
      "isAtBottom:",
      isAtBottom
    );
    if (isAtBottom && messages.length > 0) {
      console.log("[ConversationView] Scrolling to bottom");
      setTimeout(scrollToBottom, 100); // Small delay to ensure DOM is updated
    }
  }, [messages, isAtBottom, scrollToBottom]);

  /**
   * Handle WebSocket updates for real-time messages.
   */
  useWebSocketMessages(
    "llm_monitoring",
    useCallback(
      (data: any) => {
        if (data.workflow_id !== parseInt(workflowId)) return;

        console.log(
          "[ConversationView] WebSocket message received:",
          data.message_type,
          "from",
          data.agent_name
        );

        // SWRM-164: Update active processors tracking
        updateActiveProcessors(data);

        // SWRM-164: Hide request messages for oneshot documents
        if (
          data.message_type === "request" &&
          data.agent_type.toLowerCase().includes("oneshot")
        ) {
          console.log("[ConversationView] Skipping oneshot request message");
          return; // Skip this message
        }

        // SWRM-164: Hide the error message box for human input requests (we'll style the actual orchestrator message instead)
        if (
          data.message_type === "error" &&
          data.content?.response_content?.includes("HUMAN INPUT REQUESTED")
        ) {
          return;
        }

        // Skip tool_response messages - the agent already acknowledges tool completion
        if (data.message_type === "tool_response") {
          return;
        }

        // Don't skip processor_complete messages - show them with grey styling for observability

        // Transform the WebSocket message to ConversationMessage format
        const stepId = extractStepId(data.agent_name);
        const processorType = mapAgentTypeToProcessorType(data.agent_type);
        const indentLevel = calculateIndentLevel(stepId);

        let content =
          data.content?.response_content ||
          data.content?.prompt ||
          data.content?.system_prompt ||
          "";

        // Use setMessages with callback to access current state for deduplication checks
        setMessages((prevMessages) => {
          // Check if this message already exists (by ID)
          if (data.id && prevMessages.some((m) => m.id === data.id)) {
            return prevMessages; // Skip duplicate
          }

          // Don't skip based on ID - just check for actual duplicates above
          // (The "old message" check was causing issues when workflows are reset)

          // SWRM-164: Skip LLM request messages if we already have a message from this step_id
          // (avoids duplicates when processors log both processor_start and LLM request)
          if (data.message_type === "request") {
            const existingMessagesFromStep = prevMessages.some(
              (m) => m.stepId === stepId
            );
            if (existingMessagesFromStep) {
              return prevMessages; // Skip - processor_start event already logged
            }
            content = `Starting ${data.agent_type}`;
          } else if (data.message_type === "processor_start") {
            // SWRM-164: Transform processor_start messages
            const lowerAgentType = data.agent_type.toLowerCase();
            if (lowerAgentType.includes("orchestrator")) {
              // Check if this is a resume (orchestrator with prior messages)
              const priorMessages = prevMessages.filter(
                (m) => m.agentName === data.agent_name
              );
              content =
                priorMessages.length > 0
                  ? "Resuming orchestration"
                  : "Starting orchestration";
            } else {
              // For other processors, use the response_content (already set above)
              // If empty, use a fallback
              if (!content) {
                content = "Starting processor";
              }
            }
          } else if (data.message_type === "processor_failed") {
            // Use the message as-is (already in content)
          } else if (content) {
            console.log('[ConversationView] Processing content for message type:', data.message_type, 'content:', content);
            // SWRM-164: Special handling for oneshot document responses - show preview
            if (
              data.message_type === "response" &&
              data.agent_type.toLowerCase().includes("oneshot")
            ) {
              const preview = content
                .replace(/\n+/g, " ") // Replace newlines with spaces
                .replace(/\s+/g, " ") // Collapse multiple spaces
                .trim()
                .substring(0, 150); // Take first 150 chars
              content = `Generated a document starting with "${preview}..."`;
            } else {
              // SWRM-164: Check if response contains a tool call and show both message and tool call
              try {
                const responseData =
                  typeof content === "string" ? JSON.parse(content) : content;
                
                console.log('[ConversationView] Parsed response data:', responseData);

                if (responseData?.tool_call?.name) {
                  // This is an LLM response with a tool call - show both thinking and tool call
                  const thinking = responseData.text_content || responseData.text || responseData.message || responseData.content || '';
                  const toolName = responseData.tool_call.name;
                  
                  console.log('[ConversationView] Found tool call:', toolName, 'with thinking:', thinking);
                  
                  if (thinking) {
                    content = `${thinking}\n\n📞 Calling tool: ${toolName}`;
                  } else {
                    content = `Calling tool ${toolName}`;
                  }
                }
              } catch (e) {
                // Not JSON or parsing failed - keep as-is
                console.log('[ConversationView] Failed to parse as JSON:', e);
              }
            }
          }

          const isUserMessage =
            data.message_type === "human_input" && content.includes("👤");

          const newMessage: ConversationMessage = {
            id: data.id,
            agentName: data.agent_name,
            stepId,
            processorType,
            messageType: data.message_type,
            content,
            timestamp: data.timestamp,
            indentLevel,
            isUserMessage,
          };

          lastMessageIdRef.current = data.id;
          return [...prevMessages, newMessage];
        });

        // If user is not at bottom, increment new message counter
        if (!isAtBottomRef.current) {
          setNewMessageCount((count) => count + 1);
        }
      },
      [
        workflowId,
        extractStepId,
        mapAgentTypeToProcessorType,
        calculateIndentLevel,
        updateActiveProcessors,
      ]
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-white dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading conversation...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <p className="text-red-700 dark:text-red-300">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden h-[800px] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Conversation View
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Real-time workflow execution events and LLM conversations
        </p>
      </div>

      {/* Message area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
      >
        {messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No messages yet. Workflow events will appear here as they happen.
          </div>
        ) : (
          messages.map((message) => (
            <ProcessorMessage
              key={message.id}
              message={message}
              isActive={isProcessorActive(message.stepId, message.id)}
            />
          ))
        )}
      </div>

      {/* New messages notification */}
      {newMessageCount > 0 && !isAtBottom && (
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2">
          <button
            onClick={scrollToBottom}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-colors"
          >
            <span>
              ↓ {newMessageCount} new message{newMessageCount > 1 ? "s" : ""}
            </span>
          </button>
        </div>
      )}

      {/* Chat input area (messenger-style with send button) */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-end gap-3">
          <textarea
            disabled={!isWaitingForInput || isSubmitting}
            value={humanInputValue}
            onChange={(e) => setHumanInputValue(e.target.value)}
            placeholder={
              workflowStatus === "completed" ||
              workflowStatus === "failed" ||
              workflowStatus === "archived"
                ? `Workflow ${workflowStatus}`
                : isWaitingForInput
                ? isSubmitting
                  ? "Submitting..."
                  : "Type your response..."
                : workflowStatus === "paused"
                ? "Workflow paused - use Resume button to continue"
                : "Workflow running..."
            }
            className={`flex-1 px-4 py-3 border rounded-2xl resize-none focus:outline-none ${
              isWaitingForInput && !isSubmitting
                ? "border-orange-500 dark:border-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-500"
                : "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
            rows={1}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                isWaitingForInput &&
                humanInputValue.trim() &&
                !isSubmitting
              ) {
                e.preventDefault();
                handleSubmitHumanInput();
              }
            }}
          />
          <button
            disabled={
              !isWaitingForInput || !humanInputValue.trim() || isSubmitting
            }
            onClick={handleSubmitHumanInput}
            className={`p-3 rounded-full ${
              isWaitingForInput && humanInputValue.trim() && !isSubmitting
                ? "bg-orange-500 hover:bg-orange-600 text-white cursor-pointer"
                : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
            title={
              isWaitingForInput
                ? "Send response"
                : "Human input requests will enable this when needed"
            }
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {isWaitingForInput
            ? "Workflow is waiting for your response. Press Enter or click send to continue."
            : "Human input requests will enable this field when needed"}
        </p>
      </div>
    </div>
  );
};
